// @ts-nocheck
"use client";

import { DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useTheme } from "@/context/ThemeContext";
import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Menu,
  Star,
  Maximize2,
  Minimize2,
  LayoutGrid,
  Save,
  Moon,
  Sun,
  TrendingUp,
  DollarSign,
  Target,
  Bot,
  ChevronDown,
  CandlestickChart,
  Brain,
  Activity,
  Zap,
} from "lucide-react";
// Removed import { Link, useRouter } from "@/i18n/routing";
import { cn } from "@/lib/utils";
import { useLayout } from "../layout/layout-context";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import type { Symbol } from "@/store/trade/use-binary-store";
import { wishlistService } from "@/services/trade/wishlist-service";
import { marketDataWs, type TickerData, type OrderbookData } from "@/services/trade/market-data-ws";
import { useTranslations } from "@/hooks/use-translations";
import { useSidebar } from "@/context/SidebarContext";
import { CompactWalletIndicator } from "./compact-wallet-indicator";
import { useChartData } from "../chart/chart-data-context";
import { TradingModeToggle } from "./trading-mode-toggle";
import { StrategyPositionDisplay } from "./strategy-position-display";
import type { TradingMode, StrategySignal } from "@/types/trade/strategy-signals";
import { StrategyEngine } from "@/services/trade/strategy-engine";
import { strategySignalsWs, type StrategySignalData } from "@/services/trade/strategy-signals-ws";
import { notificationService } from "@/services/trade/notification-service";
import { useUserStore } from "@/store/trade/user";

export default function TradingHeader({
  currentSymbol,
  onSymbolChange,
  marketType = "spot",
  currentChartType = "candlestick",
  onChartTypeChange,
  hideBackButton = false,
}: {
  currentSymbol?: Symbol;
  onSymbolChange?: (symbol: Symbol) => void;
  marketType?: "spot" | "futures" | "eco" | "stocks" | "feed";
  currentChartType?: string;
  onChartTypeChange?: (chartType: string) => void;
  hideBackButton?: boolean;
}) {
  const t = useTranslations("trade/components/header/trading-header");
  // Removed const router = useRouter();
  const { user } = useUserStore();

  // Get chart type from context
  const { chartType: contextChartType, setChartType: setContextChartType } = useChartData();
  const [price, setPrice] = useState("Loading...");
  const [priceChange, setPriceChange] = useState("0.00%");
  const [isPositive, setIsPositive] = useState(true);
  const [mounted, setMounted] = useState(false);
  const [precision, setPrecision] = useState(2);
  const [volume, setVolume] = useState("--");

  const [isFavorite, setIsFavorite] = useState(false);
  const [bid, setBid] = useState<string>("--");
  const [ask, setAsk] = useState<string>("--");
  const [isAutoTradingActive, setIsAutoTradingActive] = useState(true);
  const [positions, setPositions] = useState(0);
  const [winRate, setWinRate] = useState("0%");
  const [pnl, setPnl] = useState("+0.0000");

  // Trading mode state
  const [tradingMode, setTradingMode] = useState<TradingMode>("DEMO");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [demoQuantity, setDemoQuantity] = useState(0.001);
  const [liveQuantity, setLiveQuantity] = useState(0.01);

  // Strategy signals and positions
  const [activeSignals, setActiveSignals] = useState<StrategySignal[]>([]);
  const [openPositions, setOpenPositions] = useState<StrategySignal[]>([]);
  const [closedPositions, setClosedPositions] = useState<StrategySignal[]>([]);
  const [strategyNextStep, setStrategyNextStep] = useState<string>("");
  const [strategyReasoning, setStrategyReasoning] = useState<string>("");
  const strategyEngineRef = useRef<StrategyEngine | null>(null);
  const { sidebarOpen, setSidebarOpen } = useSidebar();
  const [displaySymbol, setDisplaySymbol] = useState("");
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const orderbookUnsubscribeRef = useRef<(() => void) | null>(null);

  const { theme, setTheme } = useTheme();

  // Add safe destructuring with default values
  const {
    layoutConfig = {
      leftPanel: 20,
      centerPanel: 60,
      rightPanel: 20,
      topPanel: 0,
      bottomPanel: 20,
      chartPanel: 70,
      dataPanel: 30,
      panels: {},
      panelGroups: {},
    },
    currentPreset = "",
    layoutPresets = {},
    applyPreset = () => { },
    addLayoutPreset = () => { },
  } = useLayout() || {};

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [newPresetName, setNewPresetName] = useState("");



  // Format price with appropriate precision
  const formatPrice = (price: number, precision = 2): string => {
    if (typeof price !== "number") return "Loading...";

    return price.toLocaleString(undefined, {
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
    });
  };

  // Format display symbol
  const getDisplaySymbol = (symbol: Symbol) => {
    // Handle delimiter-based formats first: BTC/USDT, BTC-USDT, BTC_USDT
    if (symbol.includes("/")) {
      return symbol; // Already in display format
    }
    if (symbol.includes("-")) {
      return symbol.replace("-", "/");
    }
    if (symbol.includes("_")) {
      return symbol.replace("_", "/");
    }

    // For symbols without delimiters (like BTCUSDT), split intelligently
    const midPoint = Math.floor(symbol.length / 2);

    // Try different split points around the middle to find a reasonable split
    for (let i = Math.max(2, midPoint - 2); i <= Math.min(symbol.length - 2, midPoint + 2); i++) {
      const base = symbol.substring(0, i);
      const quote = symbol.substring(i);

      // Prefer splits where quote is 3-4 characters (common for crypto quotes)
      if (quote.length >= 3 && quote.length <= 4) {
        return `${base}/${quote}`;
      }
    }

    // Fallback: split at midpoint
    const currency = symbol.substring(0, midPoint);
    const pair = symbol.substring(midPoint);
    return `${currency}/${pair}`;
  };

  // Format volume for display (K, M, B)
  const formatVolume = (volume: number | string): string => {
    // Convert string to number if needed
    const numVolume =
      typeof volume === "string" ? Number.parseFloat(volume) : volume;

    if (isNaN(numVolume)) return "--";

    if (numVolume >= 1_000_000_000) {
      return `${(numVolume / 1_000_000_000).toFixed(1)}B`;
    } else if (numVolume >= 1_000_000) {
      return `${(numVolume / 1_000_000).toFixed(1)}M`;
    } else if (numVolume >= 1_000) {
      return `${(numVolume / 1_000).toFixed(1)}K`;
    }
    return `${numVolume.toFixed(0)}`;
  };

  // Update display symbol when current symbol changes
  useEffect(() => {
    if (currentSymbol) {
      setDisplaySymbol(getDisplaySymbol(currentSymbol));
    } else {
      setDisplaySymbol("BTC/USDT"); // Default display if no symbol is provided
    }
  }, [currentSymbol]);

  // Extract bid/ask from orderbook data (fallback when ticker data is not available)
  const extractBidAskFromOrderbook = useCallback((orderbookData: OrderbookData) => {
    if (!orderbookData || !orderbookData.bids?.length || !orderbookData.asks?.length) {
      console.log(`[TradingHeader] No orderbook data available`);
      return;
    }

    console.log(`[TradingHeader] Orderbook data received:`, {
      bidsCount: orderbookData.bids?.length ?? 0,
      asksCount: orderbookData.asks?.length ?? 0,
      firstBid: orderbookData.bids?.[0],
      lastBid: orderbookData.bids?.[orderbookData.bids?.length - 1],
      firstAsk: orderbookData.asks?.[0],
      lastAsk: orderbookData.asks?.[orderbookData.asks?.length - 1]
    });

    // Get bid/ask from orderbook (fallback when ticker data is not available)
    // Best bid = highest bid price, Best ask = lowest ask price
    const orderbookBid = Math.max(...(orderbookData.bids ?? []).map(([price]) => price));
    const orderbookAsk = Math.min(...(orderbookData.asks ?? []).map(([price]) => price));

    console.log(`[TradingHeader] Orderbook fallback - Bid: ${orderbookBid}, Ask: ${orderbookAsk}`);

    if (orderbookBid && orderbookAsk) {
      const formattedBid = formatPrice(orderbookBid, precision);
      const formattedAsk = formatPrice(orderbookAsk, precision);

      setBid(formattedBid);
      setAsk(formattedAsk);

      console.log(`[TradingHeader] ✅ Updated bid/ask from orderbook - Bid=${formattedBid}, Ask=${formattedAsk}, Spread=${(orderbookAsk - orderbookBid).toFixed(2)}`);
    }
  }, [precision]);

  // Handle orderbook data updates
  const handleOrderbookUpdate = useCallback(
    (data: OrderbookData) => {
      if (!currentSymbol || !data) return;
      extractBidAskFromOrderbook(data);
    },
    [currentSymbol, extractBidAskFromOrderbook]
  );

  // Handle ticker data updates from the market data WebSocket service
  const handleTickerUpdate = useCallback(
    (data: TickerData) => {
      console.log(`[TradingHeader] 📊 Ticker update received for ${currentSymbol}:`, data);
      if (!currentSymbol || !data) {
        console.log(`[TradingHeader] ❌ Skipping ticker update - missing symbol or data`);
        return;
      }

      // Get price and apply precision
      console.log(`[TradingHeader] Ticker data received:`, data);

      // Cast to any to access potentially missing fields from the interface
      const tickerData = data as any;

      console.log(`[TradingHeader] Price field check - last: ${tickerData.last}, price: ${tickerData.price}, close: ${tickerData.close}`);

      // Try different price fields in order of preference
      let priceValue: number | undefined;

      console.log(`[TradingHeader] Available ticker fields:`, Object.keys(tickerData));
      console.log(`[TradingHeader] Ticker data values - last: ${tickerData.last}, price: ${tickerData.price}, close: ${tickerData.close}, bid: ${tickerData.bid}, ask: ${tickerData.ask}`);

      if (tickerData.last !== undefined && tickerData.last > 0) {
        priceValue = tickerData.last;
        console.log(`[TradingHeader] Using tickerData.last for price: ${tickerData.last}`);
      } else if (tickerData.price !== undefined && tickerData.price > 0) {
        priceValue = tickerData.price;
        console.log(`[TradingHeader] Using tickerData.price for price: ${tickerData.price}`);
      } else if (tickerData.close !== undefined && tickerData.close > 0) {
        priceValue = tickerData.close;
        console.log(`[TradingHeader] Using tickerData.close for price: ${tickerData.close}`);
      } else if (tickerData.bid !== undefined && tickerData.bid > 0) {
        priceValue = tickerData.bid;
        console.log(`[TradingHeader] Using tickerData.bid for price: ${tickerData.bid}`);
      } else if (tickerData.ask !== undefined && tickerData.ask > 0) {
        priceValue = tickerData.ask;
        console.log(`[TradingHeader] Using tickerData.ask for price: ${tickerData.ask}`);
      } else {
        console.log(`[TradingHeader] ❌ No valid price field found in ticker data`);
        console.log(`[TradingHeader] Full ticker data:`, JSON.stringify(tickerData, null, 2));
        return;
      }

      if (!priceValue) {
        console.log(`[TradingHeader] ❌ priceValue is undefined after extraction`);
        return;
      }

      // Reject obviously hardcoded mock prices: exactly 50000 regardless of change field
      const numericPrice = Number(priceValue);
      if (Math.abs(numericPrice - 50000) < 0.5) {
        console.warn(`[TradingHeader] Rejected hardcoded mock price: ${numericPrice}`);
        return;
      }

      const formattedPrice = formatPrice(priceValue, precision);

      // Get actual bid/ask from ticker data (primary source)
      if (tickerData.bid !== undefined && tickerData.ask !== undefined) {
        const formattedBid = formatPrice(tickerData.bid, precision);
        const formattedAsk = formatPrice(tickerData.ask, precision);
        setBid(formattedBid);
        setAsk(formattedAsk);
        console.log(`[TradingHeader] ✅ Updated bid/ask from ticker - Bid=${formattedBid}, Ask=${formattedAsk}, Spread=${(tickerData.ask - tickerData.bid).toFixed(2)}`);
      } else {
        console.log(`[TradingHeader] Ticker data missing bid/ask for ${currentSymbol}, will use orderbook data if available`);
      }

      setPrice(formattedPrice);

      // Handle price change percentage - use percentage field if available, otherwise calculate
      if (tickerData.percentage !== undefined) {
        const changePercent = `${tickerData.percentage >= 0 ? "+" : ""}${tickerData.percentage.toFixed(2)}%`;
        setPriceChange(changePercent);
        setIsPositive(tickerData.percentage >= 0);
      } else if (tickerData.change !== undefined && priceValue !== undefined) {
        // Calculate percentage from absolute change and current price
        const percentage = (tickerData.change / (priceValue - tickerData.change)) * 100;
        const changePercent = `${percentage >= 0 ? "+" : ""}${percentage.toFixed(2)}%`;
        setPriceChange(changePercent);
        setIsPositive(percentage >= 0);
      }

      // Handle volume (use quoteVolume if available)
      if (tickerData.quoteVolume !== undefined) {
        const formattedVolume = tickerData.quoteVolume > 1000000
          ? `${(tickerData.quoteVolume / 1000000).toFixed(1)}M`
          : tickerData.quoteVolume > 1000
            ? `${(tickerData.quoteVolume / 1000).toFixed(1)}K`
            : tickerData.quoteVolume.toFixed(0);
        setVolume(formattedVolume);
      }

      // Note: 'high' property doesn't exist in TickerData type
      // Would need to be added to the type definition if needed
    },
    [currentSymbol, precision]
  );

  // Subscribe to market data based on market type
  useEffect(() => {
    if (!mounted || !currentSymbol) return;

    // Clean up previous subscription
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    // Reset price display while loading new data
    setPrice("Loading...");
    setPriceChange("0.00%");
    setVolume("--");
    setBid("--");
    setAsk("--");

    // Subscribe to ticker data using marketType directly
    console.log(`[TradingHeader] 🔌 Subscribing to ticker data for ${currentSymbol} (${marketType})`);
    const unsubscribe = marketDataWs.subscribe<TickerData>(
      {
        symbol: currentSymbol,
        type: "ticker",
        marketType: marketType,
      },
      handleTickerUpdate
    );

    unsubscribeRef.current = unsubscribe;

    // Also subscribe to orderbook data for bid/ask prices
    const unsubscribeOrderbook = marketDataWs.subscribe<OrderbookData>(
      {
        symbol: currentSymbol,
        type: "orderbook",
        marketType: marketType,
      },
      handleOrderbookUpdate
    );

    orderbookUnsubscribeRef.current = unsubscribeOrderbook;

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      if (orderbookUnsubscribeRef.current) {
        orderbookUnsubscribeRef.current();
        orderbookUnsubscribeRef.current = null;
      }
    };
  }, [currentSymbol, marketType, mounted, handleOrderbookUpdate]);

  // Check if the current symbol is in the wishlist
  useEffect(() => {
    if (!currentSymbol) return;

    const unsubscribe = wishlistService.subscribe((wishlist) => {
      setIsFavorite(wishlist.some((item) => item.symbol === currentSymbol));
    });

    return () => unsubscribe();
  }, [currentSymbol]);

  // Set mounted state and default status
  useEffect(() => {
    setMounted(true);
    // Set default status immediately so user sees something
    if (isAutoTradingActive) {
      setStrategyNextStep('Waiting for connection...');
    }
    return () => {
      // Cleanup
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      if (orderbookUnsubscribeRef.current) {
        orderbookUnsubscribeRef.current();
        orderbookUnsubscribeRef.current = null;
      }
    };
  }, [isAutoTradingActive]);

  // Handle fullscreen toggle
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  // Handle layout preset selection
  const handlePresetSelect = (preset: string) => {
    console.log(`Selected preset: ${preset}`);
    applyPreset(preset);
  };

  // Handle save layout
  const handleSaveLayout = () => {
    if (newPresetName.trim() === "") return;

    addLayoutPreset(newPresetName, layoutConfig);
    setSaveDialogOpen(false);
    setNewPresetName("");
  };

  // Toggle favorite status
  const toggleFavorite = () => {
    if (currentSymbol) {
      wishlistService.toggleWishlist(currentSymbol);
    }
  };

  // Toggle auto-trading status
  const toggleAutoTrading = async () => {
    const newState = !isAutoTradingActive;
    console.log(`[TradingHeader] Toggling auto trading from ${isAutoTradingActive} to ${newState}`);
    setIsAutoTradingActive(newState);

    if (newState) {
      // 🔔 Auto-trading enabled
      notificationService.add({
        type: 'success',
        title: '🤖 Auto-Trading Enabled',
        message: `${strategyState.chartType.toUpperCase()} strategy is now active`,
        duration: 4000,
        metadata: {
          chartType: strategyState.chartType
        }
      });

      // Request browser notification permission
      if ('Notification' in window && Notification.permission === 'default') {
        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
          notificationService.add({
            type: 'info',
            title: 'Browser Notifications Enabled',
            message: 'You will receive desktop notifications for trading signals',
            duration: 3000
          });
        }
      }
    } else {
      // 🔔 Auto-trading disabled
      notificationService.add({
        type: 'warning',
        title: '⏸️ Auto-Trading Disabled',
        message: 'Strategy monitoring paused',
        duration: 3000
      });
    }
  };

  // Handle trading mode change
  const handleModeChange = useCallback((mode: TradingMode, passkey?: string) => {
    if (mode === "LIVE" && passkey) {
      // TODO: Validate passkey with backend
      // For now, accept any passkey
      setIsAuthenticated(true);
      setTradingMode("LIVE");
      console.log(`[Trading Mode] Switched to LIVE mode with passkey`);

      // Broadcast mode change event
      window.dispatchEvent(new CustomEvent("trading-mode-changed", {
        detail: { oldMode: tradingMode, newMode: "LIVE", isAuthenticated: true }
      }));
    } else if (mode === "DEMO") {
      setIsAuthenticated(false);
      setTradingMode("DEMO");
      console.log(`[Trading Mode] Switched to DEMO mode`);

      // Broadcast mode change event
      window.dispatchEvent(new CustomEvent("trading-mode-changed", {
        detail: { oldMode: tradingMode, newMode: "DEMO", isAuthenticated: false }
      }));
    }
  }, [tradingMode]);

  // Handle quantity change
  const handleQuantityChange = useCallback((mode: TradingMode, quantity: number) => {
    if (mode === "DEMO") {
      setDemoQuantity(quantity);
      console.log(`[Trading Mode] Demo quantity set to ${quantity}`);
    } else {
      setLiveQuantity(quantity);
      console.log(`[Trading Mode] Live quantity set to ${quantity}`);
    }
  }, []);


  // Strategy simulation state - use context chart type or prop
  const activeChartType = contextChartType || currentChartType || "candlestick";

  const [strategyState, setStrategyState] = useState({
    isActive: false,
    chartType: activeChartType,
    positions: [] as Array<{ id: string; entryPrice: number; size: number; pnl: number; isOpen: boolean }>,
    totalPnL: 0,
    winCount: 0,
    totalTrades: 0,
    marketData: { price: 0, volume: 0, timestamp: 0 }
  });

  // Initialize strategy simulation
  const initializeStrategySimulation = useCallback((chartType: string) => {
    // Reset positions for new strategy
    setPositions(0);
    setWinRate("0%");
    setPnl("+0.0000");

    setStrategyState(prev => ({
      ...prev,
      positions: [],
      totalPnL: 0,
      winCount: 0,
      totalTrades: 0
    }));

    console.log(`[TradingHeader] Initialized ${chartType} strategy simulation`);
  }, []);

  // Handle chart type changes and trigger strategy-based simulation
  const handleChartTypeChange = useCallback((newChartType: string) => {
    console.log(`[TradingHeader] 🎯 Chart type changing to: ${newChartType}`);

    // 1. Update strategy state
    setStrategyState(prev => ({
      ...prev,
      chartType: newChartType,
      isActive: true
    }));

    // 2. Update chart context so visual changes
    setContextChartType(newChartType);

    // 3. Dispatch event for advanced chart with delay to ensure listener is ready
    setTimeout(() => {
      const event = new CustomEvent("chart-type-changed", {
        detail: { chartType: newChartType }
      });
      window.dispatchEvent(event);
      console.log(`[TradingHeader] 📡 Dispatched chart-type-changed event for: ${newChartType}`);

      // Also dispatch to advanced chart context directly
      const advancedEvent = new CustomEvent("advanced-chart-type-changed", {
        detail: { chartType: newChartType }
      });
      window.dispatchEvent(advancedEvent);
      console.log(`[TradingHeader] 📡 Dispatched advanced-chart-type-changed event for: ${newChartType}`);
    }, 100);

    // 4. Reset strategy stats
    initializeStrategySimulation(newChartType);

    console.log(`[TradingHeader] ✅ Strategy activated: ${newChartType.toUpperCase()}`);
  }, [initializeStrategySimulation, setContextChartType, currentSymbol]);

  // Subscribe to backend strategy signals via WebSocket
  useEffect(() => {
    if (!mounted || !currentSymbol || !isAutoTradingActive) {
      console.log('[Strategy WS] Not subscribing - conditions not met');
      return;
    }

    console.log(`[Strategy WS] Connecting to backend for ${currentSymbol} - ${strategyState.chartType}`);

    // Connect to backend WebSocket (with userId for notifications)
    strategySignalsWs.connect(currentSymbol, strategyState.chartType, user?.id);

    // Subscribe to signals
    const unsubscribe = strategySignalsWs.subscribe((data: StrategySignalData) => {
      console.log('[Strategy WS] Received signal:', data);

      if (data.type === 'signal' && data.data.stats) {
        // Update stats from backend
        setPositions(data.data.stats.openPositions);
        setWinRate(`${data.data.stats.winRate.toFixed(0)}%`);
        setPnl(data.data.stats.totalPnL >= 0
          ? `+${data.data.stats.totalPnL.toFixed(4)}`
          : data.data.stats.totalPnL.toFixed(4)
        );

        const entryPrice = data.data.signal?.price || 0;
        const positionSize = data.data.signal?.size || 0;
        setStrategyNextStep(`Position opened at $${entryPrice.toFixed(2)}`);

        // 🔔 Show entry notification
        notificationService.add({
          type: 'success',
          title: `🎯 ${strategyState.chartType.toUpperCase()} Entry Signal`,
          message: `Position opened at $${entryPrice.toFixed(2)} (Size: ${positionSize})`,
          duration: 5000,
          metadata: {
            symbol: currentSymbol,
            chartType: strategyState.chartType,
            signalType: 'entry',
            price: entryPrice,
            amount: positionSize
          }
        });

        // Play success sound
        notificationService.playNotificationSound('info');

        // Browser notification
        notificationService.showBrowserNotification(
          `${strategyState.chartType.toUpperCase()} - Position Opened`,
          `Entry at $${entryPrice.toFixed(2)} for ${currentSymbol}`
        );

      } else if (data.type === 'exit' && data.data.stats) {
        // Update stats after exit
        setPositions(data.data.stats.openPositions);
        setWinRate(`${data.data.stats.winRate.toFixed(0)}%`);
        setPnl(data.data.stats.totalPnL >= 0
          ? `+${data.data.stats.totalPnL.toFixed(4)}`
          : data.data.stats.totalPnL.toFixed(4)
        );

        const exitPnl = data.data.pnl || 0;
        const exitPrice = data.data.exitPrice || 0;
        const isWin = exitPnl >= 0;

        setStrategyNextStep(`Position closed: ${isWin ? 'WIN' : 'LOSS'} $${exitPnl.toFixed(4)}`);

        // 🔔 Show exit notification
        notificationService.add({
          type: isWin ? 'success' : 'error',
          title: `${isWin ? '✅ WIN' : '❌ LOSS'} - ${strategyState.chartType.toUpperCase()}`,
          message: `Position closed at $${exitPrice.toFixed(2)} • P&L: ${isWin ? '+' : ''}${exitPnl.toFixed(4)}`,
          duration: 8000,
          metadata: {
            symbol: currentSymbol,
            chartType: strategyState.chartType,
            signalType: 'exit',
            price: exitPrice,
            amount: exitPnl
          }
        });

        // Play sound based on result
        notificationService.playNotificationSound(isWin ? 'success' : 'error');

        // Browser notification
        notificationService.showBrowserNotification(
          `${strategyState.chartType.toUpperCase()} - Position Closed`,
          `${isWin ? 'WIN' : 'LOSS'}: ${isWin ? '+' : ''}$${exitPnl.toFixed(4)} at $${exitPrice.toFixed(2)}`
        );

      } else if (data.type === 'subscribed') {
        console.log('[Strategy WS] Successfully subscribed');
        setStrategyNextStep('');
      }
    });

    return () => {
      console.log('[Strategy WS] Disconnecting');
      unsubscribe();
      strategySignalsWs.disconnect();
    };
  }, [mounted, currentSymbol, isAutoTradingActive, strategyState.chartType]);

  // Update strategy state when chart type changes from context
  useEffect(() => {
    if (contextChartType && contextChartType !== strategyState.chartType) {
      console.log(`[TradingHeader] Chart type changed from context: ${contextChartType}`);
      handleChartTypeChange(contextChartType);
    }
  }, [contextChartType, strategyState.chartType, handleChartTypeChange]);

  // Track current market price for backtest simulation
  const currentMarketPrice = useRef<number>(0);
  const currentVolume = useRef<number>(0);

  // Update current market price when ticker updates
  useEffect(() => {
    // Skip parsing if price is in loading state or non-numeric
    if (price === "Loading..." || price === "--") {
      console.log(`[TradingHeader] Price in loading state, skipping update`);
      return;
    }

    const priceStr = price.replace(/,/g, '');
    const parsedPrice = parseFloat(priceStr);
    console.log(`[TradingHeader] Price update - Raw: "${price}", Cleaned: "${priceStr}", Parsed: ${parsedPrice}`);

    if (!isNaN(parsedPrice) && parsedPrice > 0) {
      currentMarketPrice.current = parsedPrice;
      console.log(`[TradingHeader] ✅ Market price updated to: ${parsedPrice}`);
    } else {
      console.log(`[TradingHeader] ❌ Invalid price - isNaN: ${isNaN(parsedPrice)}, <= 0: ${parsedPrice <= 0}`);
    }
  }, [price]);

  // Initialize strategy engine when chart type changes
  useEffect(() => {
    console.log(`[Strategy Init] Mounted: ${mounted}, AutoTrading: ${isAutoTradingActive}, ChartType: ${strategyState.chartType}`);

    if (!mounted || !isAutoTradingActive) {
      console.log(`[Strategy Init] Skipping - Not ready yet`);
      return;
    }

    console.log(`[Strategy] ✅ Initializing ${strategyState.chartType.toUpperCase()} engine`);
    strategyEngineRef.current = new StrategyEngine(strategyState.chartType);

    // Reset positions when strategy changes
    setOpenPositions([]);
    setStrategyNextStep('');
    setStrategyReasoning('');

  }, [mounted, strategyState.chartType, isAutoTradingActive]);

  // Handle back to home navigation
  const handleBackToHome = () => {
    setSidebarOpen(!sidebarOpen);
  };

  if (!mounted) return null;

  // Ensure layoutPresets is an object before using Object.keys
  const presetKeys = layoutPresets ? Object.keys(layoutPresets) : [];

  // Get current price for position display
  const getCurrentPrice = () => {
    const priceStr = price.replace(/,/g, '');
    return parseFloat(priceStr) || 0;
  };

  return (
    <div className="flex flex-col border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
      {/* Main header row */}
      <div className="flex items-center justify-between px-2 py-1">
        {/* Left section - All trading information */}
        <div className="flex items-center space-x-1 md:space-x-2 flex-1 min-w-0">
          {/* Back button and Symbol with Star */}
          {!hideBackButton && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0"
              onClick={handleBackToHome}
            >
              <Menu className="h-4 w-4" />
            </Button>
          )}

          <div className="flex items-center min-w-0">
            {/* Star icon before symbol */}
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 mr-1 shrink-0"
              onClick={toggleFavorite}
            >
              <Star
                className={cn(
                  "h-4 w-4",
                  isFavorite && "text-yellow-400 fill-yellow-400"
                )}
              />
            </Button>

            <div className="font-semibold text-sm mr-2 truncate">
              {displaySymbol}
            </div>
            <div className="bg-zinc-100 dark:bg-zinc-800 text-xs px-1.5 py-0.5 rounded shrink-0 hidden sm:block">
              {marketType === "futures" ? "Futures" : marketType === "eco" ? "OTC" : "Spot"}
            </div>
          </div>

          {/* Price information */}
          <div className="flex items-center space-x-2 md:space-x-3 shrink-0">
            {/* Mobile-first ticker display */}
            <div className="flex flex-col items-end sm:items-center">
              <div className="font-bold text-base sm:text-sm text-zinc-900 dark:text-white">
                {price}
              </div>
              <div
                className={cn(
                  "text-sm sm:text-xs font-medium",
                  isPositive ? "text-green-500" : "text-red-500"
                )}
              >
                {priceChange}
              </div>
            </div>

            {/* Desktop additional info */}
            <div className="hidden lg:flex items-center text-xs text-zinc-500">
              <div>
                <div>{t("24h_vol")}</div>
                <div className="font-medium">{volume}</div>
              </div>
            </div>

            {/* Mobile compact volume display */}
            <div className="flex lg:hidden flex-col items-end text-xs text-zinc-500 dark:text-zinc-400">
              <div className="text-[10px] opacity-75">{t("24h_vol")}</div>
              <div className="font-medium text-xs">{volume}</div>
            </div>
          </div>

          {/* Current Strategy Indicator with Next Step and Dropdown */}
          <div className="flex items-center space-x-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(
                    "h-6 px-2 rounded text-xs font-medium flex items-center gap-1 transition-colors",
                    isAutoTradingActive
                      ? "bg-green-500/20 hover:bg-green-500/30 text-green-400 border-green-400/30"
                      : "bg-transparent hover:bg-zinc-700/30 text-zinc-400 border-zinc-600/30"
                  )}
                  title={strategyReasoning || `${strategyState.chartType.toUpperCase()} strategy`}
                >
                  <Bot className="h-3 w-3" />
                  {strategyState.chartType.toUpperCase()}
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuItem onClick={() => {
                  console.log('🎯 Clicked Candlestick');
                  handleChartTypeChange("candlestick");
                }}>
                  <CandlestickChart className="h-4 w-4 mr-2" />
                  Candlestick
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => {
                  console.log('🎯 Clicked OTC');
                  handleChartTypeChange("otc");
                }}>
                  <Target className="h-4 w-4 mr-2" />
                  OTC
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => {
                  console.log('🎯 Clicked AI Agents');
                  handleChartTypeChange("ai-agents");
                }}>
                  <Brain className="h-4 w-4 mr-2" />
                  AI Agents
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => {
                  console.log('🎯 Clicked Pivot Reversal');
                  handleChartTypeChange("pivot-reversal");
                }}>
                  <TrendingUp className="h-4 w-4 mr-2" />
                  Pivot Reversal
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleChartTypeChange("smart-money-concepts")}>
                  <DollarSign className="h-4 w-4 mr-2" />
                  Smart Money
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleChartTypeChange("vwap-channel")}>
                  <Activity className="h-4 w-4 mr-2" />
                  VWAP Channel
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleChartTypeChange("breakout-channels")}>
                  <Zap className="h-4 w-4 mr-2" />
                  Breakout Channels
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={toggleAutoTrading} className="text-red-500">
                  {isAutoTradingActive ? "Stop Strategy" : "Start Strategy"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Strategy Next Step Display */}
            {isAutoTradingActive && strategyNextStep && (
              <div className="text-xs text-zinc-400 dark:text-zinc-500 max-w-[200px] truncate" title={strategyReasoning}>
                {strategyNextStep}
              </div>
            )}
          </div>

          {/* Bid/Ask prices - Real-time from orderbook */}
          <div className="flex items-center space-x-1 mr-2">
            <div
              className="h-7 px-2 rounded text-xs font-medium bg-transparent text-green-400 flex items-center cursor-pointer border border-green-400/20"
              title="Bid Price (Buy Price)"
            >
              <span className="text-[10px] opacity-75 mr-1">BID</span>
              {bid}
            </div>
            <div
              className="h-7 px-2 rounded text-xs font-medium bg-transparent text-red-400 flex items-center cursor-pointer border border-red-400/20"
              title="Ask Price (Sell Price)"
            >
              <span className="text-[10px] opacity-75 mr-1">ASK</span>
              {ask}
            </div>
          </div>

          {/* Positions Summary - Position, Trades, Signals, Chart */}
          <div className="flex items-center space-x-1">
            <div
              className="h-6 px-1.5 rounded text-xs font-medium bg-transparent text-purple-400 flex items-center cursor-pointer border border-purple-400/20"
              title={`Positions: ${positions}`}
            >
              <Target className="h-3 w-3 mr-1" />
              <span className="font-bold">{positions}</span>
            </div>
            <div
              className="h-6 px-1.5 rounded text-xs font-medium bg-transparent text-indigo-400 flex items-center cursor-pointer border border-indigo-400/20 hidden sm:flex"
              title={`Win Rate: ${winRate}`}
            >
              <TrendingUp className="h-3 w-3 mr-1" />
              <span className="font-bold">{winRate}</span>
            </div>
            <div
              className="h-6 px-1.5 rounded text-xs font-medium bg-transparent text-emerald-400 flex items-center cursor-pointer border border-emerald-400/20"
              title={`PnL: ${pnl}`}
            >
              <DollarSign className="h-3 w-3 mr-1" />
              <span className="font-bold">{pnl}</span>
            </div>
          </div>

          {/* Strategy position display (inline, shown when positions are open) */}
          {openPositions.length > 0 && (
            <StrategyPositionDisplay
              strategyType={strategyState.chartType}
              openPositions={openPositions}
              closedPositions={closedPositions}
              currentPrice={getCurrentPrice()}
            />
          )}

        </div>

        {/* Right section - User controls and layout controls */}
        <div className="flex items-center space-x-1 md:space-x-2 shrink-0">
          {/* Trading Mode Toggle */}
          <TradingModeToggle
            currentMode={tradingMode}
            isAuthenticated={isAuthenticated}
            demoQuantity={demoQuantity}
            liveQuantity={liveQuantity}
            onModeChange={handleModeChange}
            onQuantityChange={handleQuantityChange}
          />

          <CompactWalletIndicator />

          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            aria-label="Toggle theme"
          >
            {theme === "dark" ? (
              <Sun className="h-3.5 w-3.5" />
            ) : (
              <Moon className="h-3.5 w-3.5" />
            )}
          </Button>

          {/* Layout Controls */}
          <div className="hidden md:block">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                >
                  <LayoutGrid className="h-3.5 w-3.5 mr-1" />
                  <span className="hidden lg:inline">
                    {currentPreset || "Default"}
                  </span>
                  <span className="lg:hidden">{t("trading_pro")}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {presetKeys.map((preset) => (
                  <DropdownMenuItem
                    key={preset}
                    onClick={() => handlePresetSelect(preset)}
                    className={cn(
                      "text-xs cursor-pointer",
                      currentPreset === preset && "bg-zinc-100 dark:bg-zinc-800"
                    )}
                  >
                    {preset}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setSaveDialogOpen(true)}
                  className="text-xs cursor-pointer"
                >
                  <Save className="h-3.5 w-3.5 mr-1" />
                  {t("save_current_layout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7 hidden sm:flex"
            onClick={toggleFullscreen}
          >
            {isFullscreen ? (
              <Minimize2 className="h-3.5 w-3.5" />
            ) : (
              <Maximize2 className="h-3.5 w-3.5" />
            )}
          </Button>
        </div>
      </div>

      {/* Save Layout Dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t("save_layout_preset")}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <label htmlFor="name" className="text-right">
                {t("Name")}
              </label>
              <Input
                id="name"
                value={newPresetName}
                onChange={(e) => setNewPresetName(e.target.value)}
                className="col-span-3"
                placeholder="My Custom Layout"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" onClick={handleSaveLayout}>
              {t("Save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
